import React, { useState, useEffect, useRef } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import DetailScreen from "./DetailScreen";
import { RateBadge } from "./StoreInfo";
import { getMapInfo } from "../../Services/StoreService";
import { useSearchContext } from "../../context/searchContext";
import { getMarkerIcon } from "../common/map";
import NoImage from "../../assets/images/NoImage.webp";

function MobileStoreCard({ info, onDetail }) {
    return (
        <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            zIndex: 10, p: 1.5,
            pointerEvents: 'none',
        }}>
            <Box sx={{
                bgcolor: 'var(--paper)',
                borderRadius: '16px',
                p: 1.25,
                boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                display: 'flex', gap: 1.25, alignItems: 'center',
                pointerEvents: 'auto',
            }}>
                <Box sx={{
                    width: 72, minWidth: 72, height: 64,
                    borderRadius: '10px', overflow: 'hidden',
                    flexShrink: 0, bgcolor: 'var(--surface-2)',
                }}>
                    <img
                        src={info.image || NoImage}
                        alt={info.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 10, color: 'var(--muted)', mb: 0.25 }}>
                        {info.category}
                    </Typography>
                    <Typography sx={{
                        fontSize: 14, fontWeight: 700, color: 'var(--ink)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        mb: 0.5,
                    }}>
                        {info.name}
                    </Typography>
                    <RateBadge rate={info.recommend} />
                </Box>
                <Box
                    component="button"
                    onClick={onDetail}
                    sx={{
                        px: '14px', py: '8px',
                        border: 'none',
                        bgcolor: 'var(--accent)', color: 'white',
                        borderRadius: '10px',
                        fontSize: 12, fontWeight: 700,
                        cursor: 'pointer', fontFamily: 'inherit',
                        flexShrink: 0,
                    }}
                >
                    詳細
                </Box>
            </Box>
        </Box>
    );
}

function UseGoogleMap({ height = '100%', onPinClick }) {
    const theme = useTheme();
    const isMobile = !useMediaQuery(theme.breakpoints.up('md'));

    const [center, setCenter] = useState({ lat: 34.6939082, lng: 135.193093 });
    const [selectedMapInfo, setSelectedMapInfo] = useState(null);
    const [isDetailVisible, setIsDetailVisible] = useState(false);
    const [mapInfos, setMapInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mapReady, setMapReady] = useState(false);

    const mapRef = useRef(null);
    const clustererRef = useRef(null);

    const { searchKeyword, searchCategory, searchArea, searchRecommend, searchEatinTakeout } = useSearchContext();

    useEffect(() => {
        const fetchMapInfo = async () => {
            setLoading(true);
            const data = await getMapInfo(searchKeyword, searchCategory, searchArea, searchRecommend, searchEatinTakeout);
            setMapInfo(data);
            setLoading(false);
        };
        fetchMapInfo();
    }, [searchKeyword, searchCategory, searchArea, searchRecommend, searchEatinTakeout]);

    useEffect(() => {
        if (!mapReady || !mapRef.current) return;

        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
            clustererRef.current = null;
        }

        if (!mapInfos || mapInfos.length === 0) return;

        const markers = mapInfos.map(info => {
            const marker = new window.google.maps.Marker({
                position: { lat: parseFloat(info.lat), lng: parseFloat(info.lng) },
                icon: {
                    url: getMarkerIcon(info.category),
                    scaledSize: new window.google.maps.Size(40, 40),
                },
                title: info.name,
            });
            marker.addListener('click', () => {
                setSelectedMapInfo(info);
                setIsDetailVisible(false);
                if (onPinClick) onPinClick(info.id);
            });
            return marker;
        });

        const clusterer = new MarkerClusterer({ map: mapRef.current, markers });
        clustererRef.current = clusterer;

        return () => {
            clusterer.clearMarkers();
            clustererRef.current = null;
        };
    }, [mapInfos, mapReady]);

    const handleMapLoad = (map) => {
        mapRef.current = map;
        const updateCenter = () => {
            const newCenter = map.getCenter();
            setCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
        };
        map.addListener("dragend", updateCenter);
        map.addListener("zoom_changed", updateCenter);
        setMapReady(true);
    };

    return (
        <Box id="map" sx={{ height, position: 'relative' }}>
            {loading && (
                <Box sx={{
                    position: 'absolute', inset: 0, zIndex: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'rgba(244,236,224,0.65)',
                    color: 'var(--muted)', fontSize: 13,
                    pointerEvents: 'none',
                }}>
                    読み込み中…
                </Box>
            )}
            <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={center}
                zoom={15}
                onLoad={handleMapLoad}
                onClick={() => setSelectedMapInfo(null)}
                options={{
                    gestureHandling: "greedy",
                    clickableIcons: false,
                }}
            >
            </GoogleMap>
            {isMobile && selectedMapInfo && !isDetailVisible && (
                <MobileStoreCard
                    info={selectedMapInfo}
                    onDetail={() => setIsDetailVisible(true)}
                />
            )}
            {isDetailVisible && selectedMapInfo && (
                <DetailScreen
                    id={selectedMapInfo.id}
                    onClose={() => setIsDetailVisible(false)}
                />
            )}
        </Box>
    );
}

export default UseGoogleMap;
